import { useEffect, useRef } from "react";
import { Form, Flex, Alert } from "antd"
import UserRoleDropdown from "../../UserRoleDropdown";
import { useSelector } from "react-redux";
import { InfoCircleOutlined } from "@ant-design/icons";
import { getActiveSetValue, getAllWidgets, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { useTranslation } from "react-i18next";

const getNormalizedUserValue = (value) => {
    if (!value) {
        return null
    }
    if (typeof value === "string") {
        return { user: [value], role: [], group: [] }
    }
    if (Array.isArray(value)) {
        return { user: value.filter(Boolean), role: [], group: [] }
    }
    if (typeof value === "object") {
        return {
            user: Array.isArray(value.user) ? value.user : [],
            role: Array.isArray(value.role) ? value.role : [],
            group: Array.isArray(value.group) ? value.group : [],
            ...(Array.isArray(value.userPicker) ? { userPicker: value.userPicker } : {}),
            ...(Array.isArray(value.dropdownItem) ? { dropdownItem: value.dropdownItem } : {})
        }
    }
    return null
}

const getDropdownItemValue = (formValues = {}, dropdownItemId = "", matchedRows = {}) => {
    if (!dropdownItemId) {
        return undefined
    }
    if (Object.prototype.hasOwnProperty.call(formValues, dropdownItemId)) {
        return formValues[dropdownItemId]
    }
    if (typeof dropdownItemId === "string" && dropdownItemId.includes("::")) {
        const [tableId, columnId] = dropdownItemId.split("::")
        const tableRows = formValues?.[tableId]
        if (Array.isArray(tableRows)) {
            const matchedRowIndex = matchedRows?.[tableId]
            if (Number.isInteger(matchedRowIndex) && tableRows[matchedRowIndex]) {
                const matchedRowValue = tableRows[matchedRowIndex][columnId]
                if (matchedRowValue !== undefined && matchedRowValue !== null && matchedRowValue !== "") {
                    return matchedRowValue
                }
                return undefined
            }
            const matchedRow = tableRows.find((row) => row && row[columnId])
            if (matchedRow) {
                return matchedRow[columnId]
            }
        }
        if (Object.prototype.hasOwnProperty.call(formValues, columnId)) {
            return formValues[columnId]
        }
    }
    return undefined
}

const getDropdownItemValues = (formValues = {}, dropdownItemId = "", matchedRows = {}) => {
    if (!dropdownItemId) {
        return undefined
    }
    if (Object.prototype.hasOwnProperty.call(formValues, dropdownItemId)) {
        return formValues[dropdownItemId]
    }
    if (typeof dropdownItemId === "string" && dropdownItemId.includes("::")) {
        const [tableId, columnId] = dropdownItemId.split("::")
        const tableRows = formValues?.[tableId]
        if (Array.isArray(tableRows)) {
            const matchedRowIndex = matchedRows?.[tableId]
            if (Number.isInteger(matchedRowIndex) && tableRows[matchedRowIndex]) {
                return tableRows[matchedRowIndex][columnId]
            }
            return tableRows
                .map((row) => row?.[columnId])
                .filter((value) => value !== undefined && value !== null && value !== "")
        }
        if (Object.prototype.hasOwnProperty.call(formValues, columnId)) {
            return formValues[columnId]
        }
    }
    return undefined
}

const getCandidateEmailKeys = (field = null, masterFields = []) => {
    const candidates = []
    const mappedColumn = typeof field?.mappedColumn === "string" ? field.mappedColumn : ""
    if (mappedColumn) {
        if (/email/i.test(mappedColumn)) {
            candidates.push(mappedColumn)
        }
        const mappedToEmail = mappedColumn.replace(/name/ig, "Email")
        if (mappedToEmail !== mappedColumn) {
            candidates.push(mappedToEmail)
        }
    }

    if (Array.isArray(masterFields)) {
        masterFields.forEach((masterField) => {
            const label = typeof masterField?.label === "string" ? masterField.label : ""
            if (label && /email/i.test(label)) {
                candidates.push(label)
            }
        })
    }

    return Array.from(new Set(candidates.filter(Boolean)))
}

const extractEmailFromRow = (row = {}, candidateKeys = []) => {
    if (!row || typeof row !== "object") {
        return null
    }
    const normalizedCandidates = (Array.isArray(candidateKeys) ? candidateKeys : [])
        .map((key) => String(key || "").toLowerCase().trim())
        .filter(Boolean)
    for (const key of normalizedCandidates) {
        const foundKey = Object.keys(row).find((rowKey) => String(rowKey || "").toLowerCase().trim() === key)
        if (foundKey && typeof row[foundKey] === "string" && row[foundKey].includes("@")) {
            return row[foundKey]
        }
    }
    for (const key of Object.keys(row)) {
        const value = row[key]
        if (typeof value === "string" && value.includes("@")) {
            return value
        }
    }
    return null
}

const getSourceDropdownConfig = (dropdownItemId = "", formElements = []) => {
    const allWidgets = getAllWidgets(formElements)
    if (dropdownItemId && dropdownItemId.includes("::")) {
        const [tableId, columnId] = dropdownItemId.split("::")
        const tableWidget = allWidgets.find((widget) => widget.id === tableId)
        const column = tableWidget?.columns?.find((col) => col?.id === columnId)
        return column || null
    }
    return allWidgets.find((widget) => widget.id === dropdownItemId) || null
}

const resolveDropdownOptionToEmail = (dropdownItemId = "", selectedValue, formElements = [], masterList = []) => {
    if (!dropdownItemId || selectedValue === undefined || selectedValue === null || selectedValue === "") {
        return selectedValue
    }
    if (Array.isArray(selectedValue)) {
        return selectedValue.map((value) => resolveDropdownOptionToEmail(dropdownItemId, value, formElements, masterList))
    }
    if (typeof selectedValue !== "string") {
        return selectedValue
    }
    if (selectedValue.includes("@")) {
        return selectedValue
    }

    const field = getSourceDropdownConfig(dropdownItemId, formElements)
    const options = Array.isArray(field?.options) ? field.options : []
    const matchedOption = options.find((option) => option?.value == selectedValue)
    const selectedMaster = field?.parentTable
        ? (Array.isArray(masterList) ? masterList : []).find((master) => master?.dataId === field.parentTable)
        : null
    const masterFields = Array.isArray(selectedMaster?.fields) ? selectedMaster.fields : []
    const candidateEmailKeys = getCandidateEmailKeys(field, masterFields)

    const optionRowEmail = extractEmailFromRow(matchedOption?.masterRow, candidateEmailKeys)
    if (optionRowEmail) {
        return optionRowEmail
    }
    if (typeof matchedOption?.email === "string" && matchedOption.email.includes("@")) {
        return matchedOption.email
    }
    if (typeof matchedOption?.label === "string" && matchedOption.label.includes("@")) {
        return matchedOption.label
    }
    if (typeof matchedOption?.value === "string" && matchedOption.value.includes("@")) {
        return matchedOption.value
    }

    if (field?.parentTable) {
        const masterRows = Array.isArray(selectedMaster?.data) ? selectedMaster.data : []
        if (masterRows.length > 0) {
            const matchedRow = masterRows.find((row) => row?.uuid == selectedValue)
                || masterRows.find((row) => row?.[field?.mappedColumn] == matchedOption?.label)
            const matchedRowEmail = extractEmailFromRow(matchedRow, candidateEmailKeys)
            if (matchedRowEmail) {
                return matchedRowEmail
            }
        }
    }

    return undefined
}

const hasSameSelection = (left, right) => {
    return JSON.stringify(left || null) === JSON.stringify(right || null)
}

const normalizeToArray = (value) => {
    if (Array.isArray(value)) {
        return value
    }
    if (value === undefined || value === null || value === "") {
        return []
    }
    return [value]
}

const UserInput = (props) => {
    const { id,
        label = "",
        placeholder = "",
        helpText = "",
        mandatory = false,
        span = 8,
        readOnly,
        previewMode,
        formValues,
        form,
        width,
        setValueData = [],
        isRequestor,
        formType,
        ...rest
    } = props
    const { userInfo } = useSelector(state => state.user);
    const { masterList = [] } = useSelector(state => state.masterData)
    const { t } = useTranslation();
    // const [conditionIndex, setConditionIndex] = useState()
    const tooltip = helpText
        ? { title: helpText, icon: <InfoCircleOutlined /> }
        : undefined;

    let validation = [
        {
            validator: (_, value) => {
                // Skip validation for public forms since user selection is not available
                if (formType === 'public') {
                    return Promise.resolve();
                }

                if (!value || typeof value !== 'object') {
                    return Promise.reject(new Error(`${label} is required`));
                }
                let selectedValues = []
                Object.keys(value).forEach((key) => {
                    if (Array.isArray(value[key]) && value[key].length > 0) {
                        selectedValues = selectedValues.concat(value[key]);
                    }
                });
                if (selectedValues.length > 0) {
                    return Promise.resolve();
                }
                return Promise.reject(new Error(label + " is required"));
            },
        },
    ]
    const rules = (mandatory && previewMode) ? validation : undefined;
    const disabled = previewMode && (readOnly || isRequestor) ? true : false

    let activeSetValue = getActiveSetValue(setValueData, true)

    // The selection the condition last wrote, so it can be taken back once the
    // condition stops passing without discarding one the user made since.
    const appliedFormValueRef = useRef(undefined)

    useEffect(() => {
        if (activeSetValue && form && form.setFieldValue) {
            let defaultValues = activeSetValue.value
            if (typeof defaultValues === "string") {
                try {
                    defaultValues = JSON.parse(defaultValues)
                } catch (e) {
                    defaultValues = getNormalizedUserValue(defaultValues)
                }
            }
            if (defaultValues && defaultValues.userPicker && defaultValues.userPicker.length > 0) {
                let newResult = {}
                if (!hasSameSelection(formValues?.[id], newResult)) {
                    form?.setFieldValue(id, newResult)
                }
                appliedFormValueRef.current = newResult
                // setConditionIndex(activeSetValue.actionBlockIndex)
            } else if (defaultValues?.dropdownItem?.length > 0) {
                const dropdownItemId = defaultValues.dropdownItem[0]
                let currentItemValue = getDropdownItemValues(formValues, dropdownItemId, activeSetValue?.matchedRows)
                const valuesAsArray = normalizeToArray(currentItemValue)
                const resolvedValues = valuesAsArray
                    .map((value) => resolveDropdownOptionToEmail(dropdownItemId, value, rest.formElements, masterList))
                    .flatMap((value) => normalizeToArray(value))
                    .filter(Boolean)
                const uniqueValues = Array.from(new Set(resolvedValues))
                let normalizedValue = getNormalizedUserValue(uniqueValues.length > 0 ? uniqueValues : currentItemValue)
                if (rest.allowSingleUser && normalizedValue?.user?.length > 1) {
                    normalizedValue = getNormalizedUserValue([normalizedValue.user[0]])
                }
                if (normalizedValue && !hasSameSelection(formValues?.[id], normalizedValue)) {
                    form.setFieldValue(id, normalizedValue)
                } else if (!normalizedValue && !hasSameSelection(formValues?.[id], null)) {
                    form.setFieldValue(id, null)
                }
                appliedFormValueRef.current = normalizedValue || null
            } else if (defaultValues) {
                if (defaultValues.dropdownItem) {
                    delete defaultValues.dropdownItem
                }
                let normalizedValue = getNormalizedUserValue(defaultValues)
                let nextValue = normalizedValue || defaultValues
                if (!hasSameSelection(formValues?.[id], nextValue)) {
                    form?.setFieldValue(id, nextValue)
                }
                appliedFormValueRef.current = nextValue
            } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: formValues?.[id], appliedValue: appliedFormValueRef.current })) {
                // The condition that populated this field no longer passes, so drop
                // the selection it made — or restore the field default if one exists.
                const fallback = getConditionalRevertValue(id, rest.defaultFormValues)
                const nextValue = fallback ? (getNormalizedUserValue(fallback) || fallback) : null
                if (!hasSameSelection(formValues?.[id], nextValue)) {
                    form.setFieldValue(id, nextValue)
                }
                appliedFormValueRef.current = undefined
            }

        } 
    }, [activeSetValue, formValues, id, masterList])

    useEffect(() => {
        if (isRequestor && !formValues[id] && previewMode && userInfo.email) {
            form?.setFieldValue(id, { user: [userInfo.email] })
        }
    }, [isRequestor, userInfo])

    useEffect(() => {
    }, [formValues])

    let tabsRequired = [], usersList = null, groupsList = null, rolesList = null, pickerList = null;
    if (!rest.disableUser) {
        tabsRequired.push('user')
    }
    if (!rest.disableGroup) {
        tabsRequired.push('group')
    }
    
    if (!rest.disableUserWidget) {
        tabsRequired.push('userPicker')
    }
    if (rest?.userValues) {

        let values = typeof rest.userValues === "string" ? JSON.parse(rest.userValues) : rest.userValues;
        if (values.user) {
            usersList = values.user
        }
        if (values.group) {
            groupsList = values.group
        }
        if (values.role) {
            rolesList = values.role
        }
        if (values.userPicker) {
            pickerList = values.picker
        }
    }

    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item
                    name={id}
                    label={label}
                    required={mandatory}
                    style={{ textAlign: "center" }}
                    tooltip={tooltip}
                    rules={rules}
                    className="user-picker-form-item"
                    validateTrigger="onChange"

                >
                    {formType === 'public' ? (
                        <Alert
                            message={t("User selection is not available for public forms")}
                            type="info"
                            showIcon
                            className="w-full"
                        />
                    ) : (
                        <UserRoleDropdown
                            placeholder={placeholder}
                            widgetInfo={{ isUserWidget: true }}
                            selectedOptions={formValues?.[id] && typeof formValues[id] != "string" ? formValues[id] : { user: [], group: [], role: [] }}
                            getSpaceUsers={(props.parentTable) ? false : true}
                            predefinedPickerList={pickerList}
                            predefinedUsersList={usersList}
                            isDisabled={disabled}
                            tabsRequired={tabsRequired}
                            onSelect={(value) => {
                                form.setFieldValue(id, value)
                                if (form.validateFields) {
                                    form.validateFields([id])
                                }

                            }}
                            mode={rest.allowSingleUser ? "SINGLE" : "MULTIPLE"}
                        />
                    )}
                </Form.Item>
            </Flex>
        </div>
    )
}

export default UserInput
